import React from "react";
import { formatDisplayDate, formatScore } from "../../utils/chartFormatters";
import type { Bucket } from "../../types";

interface LineChartTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
  // Recharts merges its injected props (active/payload/label) over this when it
  // clones the element, so the bucket passed at <Tooltip content=...> survives.
  bucket?: Bucket;
  // criteria → evaluator-name label (survives the clone like `bucket`). Each
  // payload entry's `name` is the series criteria (the Line's `name` prop).
  labels?: Record<string, string>;
}

/**
 * Custom tooltip component for the line chart.
 * Pure presentational component following Component Composition pattern.
 */
export function LineChartTooltip({
  active,
  payload,
  label,
  bucket = "day",
  labels = {},
}: LineChartTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  // Concise hover (016 §5): date · evaluator · score only. The full criteria
  // description is surfaced on the legend instead, so the tooltip no longer
  // covers the chart.
  return (
    <div className="max-w-xs rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md">
      <p className="mb-2 text-sm font-medium">{formatDisplayDate(label || "", bucket)}</p>
      {payload.map((entry, index) => {
        const criteria = entry.name;
        const evaluatorName = labels[criteria] ?? criteria;
        return (
          <div
            key={index}
            className="mb-1 flex items-center gap-2 text-sm last:mb-0"
            style={{ color: entry.color }}
          >
            <span className="font-medium">{evaluatorName}:</span>
            <span>{formatScore(entry.value)}</span>
          </div>
        );
      })}
    </div>
  );
}
